// The console's Fluent themes, built from the brand tokens (vpn repo docs/mockups/lib/tokens.mjs).
//
// Fluent writes every key of the theme object as a CSS variable on the provider, so the
// VIPPER-only roles (canvas wash, pane, tints, state colours) ride along as `--vipper*`
// variables and are read through `vt` the same way Fluent's own `tokens` are.
import { createDarkTheme, createLightTheme, type BrandVariants, type Theme } from '@fluentui/react-components';

/** Fluent's web ramp: brand80 is the token brand #0F6CBD. */
const brand: BrandVariants = {
  10: '#061724',
  20: '#082338',
  30: '#0A2E4A',
  40: '#0C3B5E',
  50: '#0E4775',
  60: '#0F548C',
  70: '#115EA3',
  80: '#0F6CBD',
  90: '#2886DE',
  100: '#479EF5',
  110: '#62ABF5',
  120: '#77B7F7',
  130: '#96C6FA',
  140: '#B4D6FA',
  150: '#CFE4FA',
  160: '#EBF3FC',
};

const fonts = {
  fontFamilyBase: "Inter, 'Segoe UI Variable Text', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', 'Cascadia Code', ui-monospace, SFMono-Regular, Consolas, 'DejaVu Sans Mono', monospace",
  fontFamilyNumeric: "'JetBrains Mono', 'Cascadia Code', ui-monospace, Consolas, monospace",
};

/** The VIPPER roles that have no Fluent token. Same keys in both themes. */
interface Palette {
  canvas: string;
  washA: string;
  washB: string;
  pane: string;
  paneShadow: string;
  surface2: string;
  surface3: string;
  brandTint: string;
  fg1: string;
  fg2: string;
  fg3: string;
  fg4: string;
  brand: string;
  cyan: string;
  violet: string;
  red: string;
  green: string;
  orange: string;
  onWarm: string;
  tintGreen: string;
  tintCyan: string;
  tintRed: string;
  tintOrange: string;
  tintNeutral: string;
}

// Green and orange are absolute in meaning, so they are the same hex in both themes.
const invariant = { green: '#3FB950', orange: '#F28C28', onWarm: '#1B1D21' };

const light: Palette = {
  canvas: '#F2F4F7',
  washA: 'rgba(212,228,245,0.45)',
  washB: 'rgba(226,224,244,0.35)',
  pane: 'rgba(255,255,255,0.86)',
  paneShadow: 'rgba(17,24,39,0.06)',
  surface2: '#EEF0F3',
  surface3: '#F7F8FA',
  brandTint: '#EBF3FC',
  fg1: '#1B1D21',
  fg2: '#454B54',
  fg3: '#7C838D',
  fg4: '#AEB4BC',
  brand: '#0F6CBD',
  cyan: '#0BA5C2',
  violet: '#7A3FBF',
  red: '#C42B3C',
  ...invariant,
  tintGreen: 'rgba(63,185,80,0.14)',
  tintCyan: 'rgba(11,165,194,0.12)',
  tintRed: 'rgba(196,43,60,0.09)',
  tintOrange: 'rgba(242,140,40,0.14)',
  tintNeutral: 'rgba(124,131,141,0.10)',
};

const dark: Palette = {
  canvas: '#0F1113',
  washA: 'rgba(18,58,99,0.45)',
  washB: 'rgba(43,37,89,0.40)',
  pane: 'rgba(26,28,32,0.86)',
  paneShadow: 'rgba(0,0,0,0.4)',
  surface2: '#26292D',
  surface3: '#202326',
  brandTint: '#1D2B38',
  fg1: '#E8EAED',
  fg2: '#BAC0C7',
  fg3: '#8A929B',
  fg4: '#5A6169',
  brand: '#4FA3E8',
  cyan: '#22E4FF',
  violet: '#C77DFF',
  red: '#FF4D6A',
  ...invariant,
  tintGreen: 'rgba(63,185,80,0.12)',
  tintCyan: 'rgba(34,228,255,0.12)',
  tintRed: 'rgba(255,77,106,0.11)',
  tintOrange: 'rgba(242,140,40,0.13)',
  tintNeutral: 'rgba(138,146,155,0.12)',
};

const varName = (k: string) => `vipper${k[0]!.toUpperCase()}${k.slice(1)}`;

/** `var(--vipperX)` for every palette role: use in makeStyles like Fluent's `tokens`. */
export const vt = Object.fromEntries(Object.keys(light).map((k) => [k, `var(--${varName(k)})`])) as Record<
  keyof Palette,
  string
>;

function build(base: Theme, p: Palette): Theme {
  const vars = Object.fromEntries(Object.entries(p).map(([k, v]) => [varName(k), v]));
  return {
    ...base,
    ...fonts,
    colorNeutralForeground1: p.fg1,
    colorNeutralForeground2: p.fg2,
    colorNeutralForeground3: p.fg3,
    colorNeutralForeground4: p.fg4,
    colorNeutralBackground2: p.canvas,
    colorNeutralStroke2: p.surface2,
    colorNeutralStroke3: p.surface2,
    // Panes 12, tiles 8; chips and buttons are fully round (borderRadiusCircular) per component.
    borderRadiusXLarge: '12px',
    borderRadiusLarge: '8px',
    // Extra keys become `--vipper*` CSS variables on the provider (see `vt`).
    ...(vars as Partial<Theme>),
  };
}

const darkBase = createDarkTheme(brand);
export const lightTheme: Theme = build(createLightTheme(brand), light);
export const darkTheme: Theme = build(
  {
    ...darkBase,
    // The dark brand is the token #4FA3E8 rather than the ramp's brand100.
    colorBrandForeground1: dark.brand,
    colorBrandForegroundLink: dark.brand,
    colorBrandForegroundLinkHover: brand[110],
    colorNeutralForeground2BrandHover: dark.brand,
    colorNeutralForeground2BrandSelected: dark.brand,
    colorCompoundBrandForeground1: dark.brand,
    colorCompoundBrandStroke: dark.brand,
  },
  dark,
);
